// AVN-1396: the dating page that opens the free chat window dispatches this on window with what dating-api's
// openFreeChatWindow returned: `{ endsAt, secondsLeft }`.
export const FREE_CHAT_WINDOW_OPENED_EVENT = 'freeChatWindowOpened'

/**
 * The window's end (and the discount's) reaches the widget from two places - getConfig and the page that opened the
 * window - and in either order. Neither is ever shortened, so the later end wins: a getConfig that was already in
 * flight when the window opened must not wipe out what the page has just reported.
 */
export function latestFreeChatWindowEndsAt(current, incoming) {
  const next = Number(incoming) > 0 ? Number(incoming) : null

  if (!current) {
    return next
  }

  return next && next > current ? next : current
}

/** Side C's discount. Matches the one baked into the "_FCW" products, so changing one means changing the other. */
export const FREE_CHAT_WINDOW_DISCOUNT = 20

/**
 * Matches dating-api's FreeDailyChatWindowABCTest::DISCOUNT_GRACE_IN_SECONDS: how long side C's discount outlives the
 * window. Only used where nothing reports the discount itself - openFreeChatWindow reports the window alone.
 */
export const FREE_CHAT_WINDOW_DISCOUNT_GRACE_SECONDS = 15 * 60

const endIn = (secondsLeft, now) => (Number(secondsLeft) > 0 ? now + Number(secondsLeft) : null)

/**
 * When the window and side C's discount end, on this device's clock. The server enforces both on its own clock, which
 * the device's may not match, so it reports how long each has left, and that is anchored to `now` on arrival. Where
 * only the window's timestamp is known (an older server), it is used as it is; where the discount is not reported
 * (openFreeChatWindow), it ends a fixed grace after the window.
 */
export function freeChatWindowEnds({ secondsLeft, endsAt, discountSecondsLeft } = {}, now) {
  const windowEndsAt = secondsLeft != null ? endIn(secondsLeft, now) : Number(endsAt) > 0 ? Number(endsAt) : null
  const discountEndsAt = discountSecondsLeft != null
    ? endIn(discountSecondsLeft, now)
    : windowEndsAt && windowEndsAt + FREE_CHAT_WINDOW_DISCOUNT_GRACE_SECONDS

  return { endsAt: windowEndsAt, discountEndsAt }
}

/**
 * The window has run out and its expiry popup has not been shown yet. Keyed on the window's end, so a remount of the
 * header - or a second header showing the same timer - cannot show it again for the same window.
 */
export function shouldShowFreeChatWindowExpiry(endsAt, now, expiryShownFor) {
  return !!endsAt && endsAt <= now && expiryShownFor !== endsAt
}

/** Matches dating's FreeDailyChatWindowABCTest::WINDOW_DURATION_IN_SECONDS. Only used to draw how much of the window is left. */
export const FREE_CHAT_WINDOW_DURATION_SECONDS = 10 * 60

/** Seconds left on side C's discount; 0 once it is over. */
export function freeChatWindowDiscountSecondsLeft(discountEndsAt, now) {
  return discountEndsAt ? Math.max(0, discountEndsAt - now) : 0
}

/** Whole minutes left on side C's discount, rounded up so the popup never promises less than what is left; 0 once over. */
export function freeChatWindowDiscountMinutesLeft(discountEndsAt, now) {
  return Math.ceil(freeChatWindowDiscountSecondsLeft(discountEndsAt, now) / 60)
}

/** "mm:ss", as the design shows both the window and the discount countdown. */
export function formatFreeChatWindowClock(seconds) {
  const total = Math.max(0, Math.floor(seconds))
  const pad = value => (value < 10 ? `0${value}` : `${value}`)

  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`
}
